import { Dispatch, SetStateAction, useContext } from 'react';
import { graphqlFetch } from '@shared/api';
import { authenticateAdminQueryDocument } from '@shared/api/documents/drafts';

import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';

import {

	ACCESS_TOKEN_EXPIRE_AT_KEY,
	USER_ROLE,
	ACCESS_TOKEN_KEY,
} from '@/constants';
import { AUTH_KEY } from '@shared/auth/Authentication.hooks';
import { AppContext } from '@/App.context';

export function useAdminAuth({ onError, setMode }: AdminAuthProps): UseMutationResult<AuthenticateResponse, Error, AuthBody, unknown> {
	const { db } = useContext(AppContext);
	const queryClient = useQueryClient();
	return useMutation<AuthenticateResponse, Error, AuthBody>({
		mutationFn: async (body) => {
			const result = await graphqlFetch(authenticateAdminQueryDocument, body);
			// authenticateAdmin is nullable in the schema (a wrong password is
			// a successful response with a null payload, not a GraphQL
			// error) -- previously unguarded, destructuring `.token` off it
			// would throw here instead of surfacing as a normal auth failure
			// via onError.
			if (!result.authenticateAdmin) {
				throw new Error('Invalid admin password');
			}
			return {
				authenticateAdmin: {
					token: result.authenticateAdmin.token,
					// Also nullable in the schema; token is otherwise useless
					// without an expiry, so treat a missing one as unusable
					// the same way a missing token is.
					expiredAt: result.authenticateAdmin.expiredAt ?? '',
				},
			};
		},
		onError,
		onSuccess: (data) => {
			const { token, expiredAt } = data.authenticateAdmin;
			if (db) {
				db.clear();
			}

			queryClient
				.cancelQueries()
				.then(() => {
					queryClient.removeQueries();
				})
				.catch(console.error)
				.finally(() => {
					localStorage.setItem(AUTH_KEY, 'true');
					localStorage.setItem(USER_ROLE, 'admin');
					localStorage.setItem(ACCESS_TOKEN_KEY, token);
					localStorage.setItem(ACCESS_TOKEN_EXPIRE_AT_KEY, expiredAt);

					setMode('logout');
				});
		},
	})
}

export type AuthMode = 'login' | 'logout';

interface AdminAuthProps {
	onError: (err: Error) => void
	setMode: Dispatch<SetStateAction<AuthMode>>;
}

export interface AuthenticateResponse {
	authenticateAdmin: {
		token: string;
		expiredAt: string;
	};
}

export interface AuthBody {
	password: string;
}
